import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useConvex, useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import CommunityShareModal from './CommunityShareModal';
import {
  AUDIOBOOK_AUDIO_FORMATS,
  AUDIOBOOK_EMOTION_OPTIONS,
  AUDIOBOOK_SPEED_OPTIONS,
  AUDIOBOOK_VOICES,
  DEFAULT_AUDIOBOOK_EMOTION,
  DEFAULT_AUDIOBOOK_FORMAT,
  DEFAULT_AUDIOBOOK_SPEED,
  DEFAULT_AUDIOBOOK_VOICE,
  formatDurationLabel,
} from '../../../shared/audiobook';
import { getErrorMessage } from '../../utils/errors';
import { isValidProjectId } from '../../utils/projectIds';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import './AudiobookStudioPage.css';

const AUDIOBOOK_STALE_RUN_MS = 120000;

const safeParseJson = (raw, fallback) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
};

const formatRunTimestamp = (value, t, locale) => {
  if (!value) return t('audiobook.notRunYet');
  return new Date(value).toLocaleString(locale || undefined);
};

const formatStatusLabel = (status, t) => {
  if (!status) return t('common.pending');
  return t(`audiobook.status.${status}`, status.charAt(0).toUpperCase() + status.slice(1));
};

const toCastingJson = (narratorVoice, castingAssignments) =>
  JSON.stringify({
    narratorVoice,
    narratorDirection: '',
    characters: castingAssignments,
    glossary: [],
  });

function AudiobookStudioPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const convex = useConvex();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const startAudiobookRun = useMutation(api.ai.startAudiobookRun);
  const recoverAudiobookRun = useMutation(api.ai.recoverAudiobookRun);
  const runAudiobook = useAction(api.ai.runAudiobook);
  const suggestAudiobookCasting = useAction(api.ai.suggestAudiobookCasting);
  const previewAudiobookVoice = useAction(api.ai.previewAudiobookVoice);

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');

  const [activeTab, setActiveTab] = useState('configure');
  const [shareModal, setShareModal] = useState(null);
  const [scopeMode, setScopeMode] = useState('full');
  const [selectedChapterPaths, setSelectedChapterPaths] = useState([]);
  const [narratorVoice, setNarratorVoice] = useState(DEFAULT_AUDIOBOOK_VOICE);
  const [pacing, setPacing] = useState(DEFAULT_AUDIOBOOK_SPEED);
  const [emotionIntensity, setEmotionIntensity] = useState(DEFAULT_AUDIOBOOK_EMOTION);
  const [audioFormat, setAudioFormat] = useState(DEFAULT_AUDIOBOOK_FORMAT);
  const [glossaryText, setGlossaryText] = useState('');
  const [castingAssignments, setCastingAssignments] = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [runs, setRuns] = useState(undefined);
  const [assets, setAssets] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [backendMessage, setBackendMessage] = useState('');
  const [isLaunching, setIsLaunching] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [hasHydratedDefaults, setHasHydratedDefaults] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const previewAudioRef = useRef(null);

  const chapterFiles = useMemo(() => {
    if (!files) return [];
    const hasCanonicalChallengeSubmission = files.some((file) => file.type === 'file' && file.path === CHALLENGE_PROJECT_SUBMISSION_PATH);
    return files
      .filter(
        (file) =>
          file.type === 'file' &&
          typeof file.content === 'string' &&
          file.content.trim().length > 0 &&
          ((file.path.startsWith('Chapters/') && file.path !== CHALLENGE_PROJECT_DRAFT_PATH) ||
            (!hasCanonicalChallengeSubmission && file.path === CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH)),
      )
      .sort(compareProjectFilesByOrder);
  }, [files]);

  const allChapterPaths = useMemo(() => chapterFiles.map((chapter) => chapter.path), [chapterFiles]);

  useEffect(() => {
    if (scopeMode === 'full') {
      setSelectedChapterPaths(allChapterPaths);
    }
  }, [scopeMode, allChapterPaths]);

  const latestRun = runs?.[0] ?? null;
  const runForDisplay = useMemo(() => {
    if (!runs || runs.length === 0) return null;
    if (activeRunId) {
      return runs.find((run) => run._id === activeRunId) ?? runs[0];
    }
    return runs[0];
  }, [runs, activeRunId]);

  useEffect(() => {
    if (hasHydratedDefaults || files === undefined || runs === undefined) return;

    if (latestRun) {
      const latestChapterPaths = Array.isArray(latestRun.scopeChapterPaths) ? latestRun.scopeChapterPaths : [];
      const validSelectedPaths = latestChapterPaths.filter((path) => allChapterPaths.includes(path));
      setScopeMode(
        validSelectedPaths.length > 0 && validSelectedPaths.length < allChapterPaths.length
          ? 'selected'
          : 'full',
      );
      setSelectedChapterPaths(validSelectedPaths.length > 0 ? validSelectedPaths : allChapterPaths);
      setNarratorVoice(latestRun.narratorVoice || DEFAULT_AUDIOBOOK_VOICE);
      setPacing(latestRun.pacing || DEFAULT_AUDIOBOOK_SPEED);
      setEmotionIntensity(latestRun.emotionIntensity || DEFAULT_AUDIOBOOK_EMOTION);
      setAudioFormat(latestRun.audioFormat || DEFAULT_AUDIOBOOK_FORMAT);
      setGlossaryText(latestRun.pronunciationGlossary || '');
      setCastingAssignments(safeParseJson(latestRun.castingJson, { characters: [] }).characters || []);
      setActiveRunId(latestRun._id);
    } else {
      setSelectedChapterPaths(allChapterPaths);
    }

    setHasHydratedDefaults(true);
  }, [allChapterPaths, files, hasHydratedDefaults, latestRun, runs]);

  useEffect(() => {
    if (!safeProjectId) {
      setRuns([]);
      return undefined;
    }

    let cancelled = false;

    const loadRuns = async () => {
      try {
        const nextRuns = await convex.query(api.ai.listAudiobookRuns, {
          projectId: safeProjectId,
          limit: 10,
        });
        if (!cancelled) {
          setRuns(nextRuns);
          setBackendMessage('');
        }
      } catch (error) {
        if (!cancelled) {
          setRuns([]);
          setBackendMessage(
            getErrorMessage(error, 'Audiobook backend is not available on the current Convex deployment.'),
          );
        }
      }
    };

    loadRuns();
    const intervalId = window.setInterval(loadRuns, latestRun?.status === 'running' ? 2500 : 4000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [convex, latestRun?.status, safeProjectId]);

  useEffect(() => {
    if (!runForDisplay?._id) {
      setAssets([]);
      return undefined;
    }

    let cancelled = false;

    const loadAssets = async () => {
      try {
        const nextAssets = await convex.query(api.ai.listAudiobookAssets, { runId: runForDisplay._id });
        if (!cancelled) {
          setAssets(nextAssets);
          setBackendMessage('');
        }
      } catch (error) {
        if (!cancelled) {
          setAssets([]);
          setBackendMessage(
            getErrorMessage(error, 'Audiobook assets could not be loaded from the current Convex deployment.'),
          );
        }
      }
    };

    loadAssets();
    const intervalId = window.setInterval(loadAssets, runForDisplay.status === 'running' ? 2500 : 4000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [convex, runForDisplay?._id, runForDisplay?.status]);

  useEffect(() => {
    if (!safeProjectId) {
      setEstimate(null);
      return undefined;
    }

    let cancelled = false;

    const loadEstimate = async () => {
      try {
        const nextEstimate = await convex.query(api.ai.estimateAudiobookRun, {
          projectId: safeProjectId,
          ...(scopeMode === 'selected' ? { chapterPaths: selectedChapterPaths } : {}),
          pronunciationGlossary: glossaryText,
          castingJson: toCastingJson(narratorVoice, castingAssignments),
          pacing,
        });
        if (!cancelled) {
          setEstimate(nextEstimate);
          setBackendMessage('');
        }
      } catch (error) {
        if (!cancelled) {
          setEstimate(null);
          setBackendMessage(
            getErrorMessage(error, 'Audiobook estimates are not available on the current Convex deployment.'),
          );
        }
      }
    };

    loadEstimate();
    return () => { cancelled = true; };
  }, [convex, safeProjectId, scopeMode, selectedChapterPaths, glossaryText, narratorVoice, castingAssignments, pacing]);

  const chapterStates = useMemo(
    () => safeParseJson(runForDisplay?.chapterStatesJson, []),
    [runForDisplay?.chapterStatesJson],
  );

  const assetByChapterPath = useMemo(
    () => Object.fromEntries(assets.map((asset) => [asset.chapterPath, asset])),
    [assets],
  );

  // Build timeline items: prefer chapterStates, fall back to assets directly
  const timelineItems = useMemo(() => {
    if (chapterStates.length > 0) return chapterStates;
    // Fallback: construct from assets when chapterStatesJson is empty
    if (assets.length > 0) {
      return assets.map((asset) => ({
        chapterPath: asset.chapterPath,
        status: 'completed',
        summary: asset.summary || '',
        estimatedDurationSeconds: asset.estimatedDurationSeconds || 0,
        error: null,
      }));
    }
    return [];
  }, [chapterStates, assets]);

  const selectedChapterCount =
    scopeMode === 'selected' ? selectedChapterPaths.length : allChapterPaths.length;
  const selectedRuntimeLabel = formatDurationLabel(estimate?.estimatedDurationSeconds || 0);
  const completedAssetCount = assets.length;
  const totalRuntimeSeconds = assets.reduce(
    (sum, asset) => sum + (asset.estimatedDurationSeconds || 0),
    0,
  );
  const activeRunBusy = isLaunching || runForDisplay?.status === 'running';
  const isBackendReady = !backendMessage;
  const isStaleRun =
    runForDisplay?.status === 'running' &&
    Date.now() - (runForDisplay?.updatedAt ?? 0) > AUDIOBOOK_STALE_RUN_MS && (
      // All chapters still pending after 2 min timeout
      (chapterStates.length > 0 &&
       chapterStates.every((state) => state.status === 'pending')) ||
      // All chapters failed but run stuck as "running"
      (chapterStates.length > 0 &&
       chapterStates.every((state) => state.status === 'failed')) ||
      // No chapter states at all
      chapterStates.length === 0
    );

  const characterCandidates = useMemo(() => {
    const names = new Set(
      castingAssignments.map((a) => a.character).filter(Boolean),
    );
    (files || []).forEach((file) => {
      if (file.type === 'file' && file.path.startsWith('Characters/')) {
        const name = file.name.replace(/\.[^.]+$/, '').trim();
        if (name) names.add(name);
      }
      if (file.path === 'Notes/story-bible.md' && typeof file.content === 'string') {
        const matches = file.content.match(/^###\s+(.+)$/gm) || [];
        matches.forEach((match) => names.add(match.replace(/^###\s+/, '').trim()));
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b)).slice(0, 12);
  }, [castingAssignments, files]);

  const setCharacterVoice = (character, voiceId) => {
    setCastingAssignments((current) => {
      const existing = current.find((item) => item.character === character);
      if (existing) {
        return current.map((item) =>
          item.character === character ? { ...item, voiceId } : item,
        );
      }
      return [
        ...current,
        { character, voiceId, rationale: t('audiobook.manualAssignment'), direction: t('audiobook.manualAssignmentDirection') },
      ];
    });
  };

  const toggleChapter = (chapterPath) => {
    if (activeRunBusy) return;
    setSelectedChapterPaths((current) =>
      current.includes(chapterPath)
        ? current.filter((path) => path !== chapterPath)
        : [...current, chapterPath].sort((a, b) => a.localeCompare(b)),
    );
  };

  const handlePreviewVoice = useCallback(async (voiceId) => {
    if (previewingVoice) return;
    setPreviewingVoice(voiceId);
    try {
      const result = await previewAudiobookVoice({ voiceId });
      const audioSrc = `data:audio/mpeg;base64,${result.audioBase64}`;
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
      const audio = new Audio(audioSrc);
      previewAudioRef.current = audio;
      audio.play();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t('audiobook.alerts.voicePreviewFailed')));
    } finally {
      setPreviewingVoice(null);
    }
  }, [previewAudiobookVoice, previewingVoice, t]);

  const handleAutoCast = async () => {
    if (!safeProjectId || isCasting || activeRunBusy) return;
    setIsCasting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await suggestAudiobookCasting({
        projectId: safeProjectId,
        ...(scopeMode === 'selected' ? { chapterPaths: selectedChapterPaths } : {}),
      });
      setNarratorVoice(result.narratorVoice || DEFAULT_AUDIOBOOK_VOICE);
      setCastingAssignments(result.characters || []);
      setGlossaryText(
        (result.glossary || []).map((entry) => `${entry.term}: ${entry.pronunciation}`).join('\n'),
      );
      setSuccessMessage(t('audiobook.castingReadyMessage', { credits: result.credits ?? 0 }));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t('audiobook.castingSuggestionsFailed')));
    } finally {
      setIsCasting(false);
    }
  };

  const handleRun = async () => {
    if (!safeProjectId || activeRunBusy) return;
    if (scopeMode === 'selected' && selectedChapterPaths.length === 0) {
      setErrorMessage(t('audiobook.alerts.selectAtLeastOne'));
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setIsLaunching(true);

    try {
      const started = await startAudiobookRun({
        projectId: safeProjectId,
        ...(scopeMode === 'selected' ? { chapterPaths: selectedChapterPaths } : {}),
        narratorVoice, pacing, emotionIntensity, audioFormat,
        pronunciationGlossary: glossaryText,
        castingJson: toCastingJson(narratorVoice, castingAssignments),
      });
      setActiveRunId(started.runId);
      setActiveTab('timeline');
      setSuccessMessage(t('audiobook.alerts.renderStarted'));
      try {
        const result = await runAudiobook({ runId: started.runId });
        if (result?.status === 'failed') {
          setErrorMessage(t('audiobook.timeline.allChaptersFailed'));
          setSuccessMessage('');
        } else if (result?.status === 'partial') {
          setErrorMessage(t('audiobook.timeline.someChaptersFailed'));
        }
      } catch (renderError) {
        setErrorMessage(getErrorMessage(renderError, t('audiobook.timeline.renderFailed')));
        setSuccessMessage('');
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t('audiobook.alerts.couldNotStart')));
    } finally {
      setIsLaunching(false);
    }
  };

  const handleRecoverRun = async () => {
    if (!runForDisplay?._id) return;
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await recoverAudiobookRun({ runId: runForDisplay._id });
      setSuccessMessage(t('audiobook.alerts.runRecovered'));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t('audiobook.alerts.couldNotRecover')));
    }
  };

  // ── Loading / Error States ──

  if (!safeProjectId) {
    return (
      <main className="ab-studio ab-loading">
        <div>{t('common.invalidProjectLink')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div>
      </main>
    );
  }

  if (project === undefined || files === undefined) {
    return <main className="ab-studio ab-loading">{t('audiobook.loadingStudio')}</main>;
  }

  if (!project) {
    return <main className="ab-studio ab-loading">{t('common.projectNotFound')}</main>;
  }

  // ── Main Render ──

  return (
    <main className="ab-studio" aria-label={t('audiobook.title')}>

      {/* Header */}
      <header className="ab-header">
        <div className="ab-header-left">
          <h1>{t('audiobook.title')} <span>— {project.title}</span></h1>
          <p className="ab-header-desc">{t('audiobook.description')}</p>
        </div>
        <div className="ab-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}`)}>{t('common.back')}</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}/editor`)}>{t('common.editor')}</button>
          <button
            className="btn btn-primary"
            onClick={handleRun}
            disabled={!isBackendReady || (activeRunBusy && !isStaleRun)}
          >
            {activeRunBusy && !isStaleRun ? t('audiobook.rendering') : t('audiobook.renderAudiobook')}
          </button>
        </div>
      </header>

      {/* Alerts */}
      {(errorMessage || backendMessage) && (
        <div className="ab-alert ab-alert--error">{errorMessage || backendMessage}</div>
      )}
      {successMessage && !errorMessage && !backendMessage && (
        <div className="ab-alert ab-alert--success">{successMessage}</div>
      )}
      {isStaleRun && (
        <div className="ab-alert ab-alert--error ab-alert--stuck">
          <span>{t('audiobook.alerts.runStuck')}</span>
          <button className="btn btn-secondary" onClick={handleRecoverRun}>{t('audiobook.alerts.recover')}</button>
        </div>
      )}

      {/* Tabs */}
      <nav className="ab-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'configure'}
          className={`ab-tab ${activeTab === 'configure' ? 'active' : ''}`}
          onClick={() => setActiveTab('configure')}
        >
          {t('audiobook.tabs.configure')}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'casting'}
          className={`ab-tab ${activeTab === 'casting' ? 'active' : ''}`}
          onClick={() => setActiveTab('casting')}
        >
          {t('audiobook.tabs.voiceAndCasting')}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'timeline'}
          className={`ab-tab ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          {t('audiobook.tabs.timeline')}{completedAssetCount > 0 && <strong>{completedAssetCount}</strong>}
        </button>
      </nav>

      {/* ── Tab Surface ── */}
      <section className="ab-surface">

      {/* ══ Configure Tab ══ */}
      {activeTab === 'configure' ? (
        <div className="ab-panel-fade" role="tabpanel" aria-label={t('audiobook.tabs.configure')}>

          {/* Stats */}
          <div className="ab-stats">
            <div className="ab-stat">
              <span className="ab-stat__label">{t('audiobook.stats.chapters')}</span>
              <strong className="ab-stat__value">{selectedChapterCount}</strong>
              <span className="ab-stat__sub">{scopeMode === 'full' ? t('audiobook.stats.fullBook') : t('audiobook.stats.selectedLabel')}</span>
            </div>
            <div className="ab-stat">
              <span className="ab-stat__label">{t('audiobook.stats.estRuntime')}</span>
              <strong className="ab-stat__value">{selectedRuntimeLabel}</strong>
              <span className="ab-stat__sub">{estimate?.totalWordCount?.toLocaleString?.() ?? 0} {t('common.words')}</span>
            </div>
            <div className="ab-stat">
              <span className="ab-stat__label">{t('audiobook.stats.rendered')}</span>
              <strong className="ab-stat__value">{completedAssetCount}</strong>
              <span className="ab-stat__sub">{t('audiobook.stats.generated', { duration: formatDurationLabel(totalRuntimeSeconds) })}</span>
            </div>
          </div>

          {/* Compact Controls */}
          <div className="ab-controls">
            <div className="ab-control-group">
              <span className="ab-section-label">{t('audiobook.controls.pacing')}</span>
              <div className="ab-pills">
                {AUDIOBOOK_SPEED_OPTIONS.map((opt) => (
                  <button key={opt.id} type="button" className={`ab-pill ${pacing === opt.id ? 'active' : ''}`} onClick={() => setPacing(opt.id)} disabled={activeRunBusy}>{opt.label}</button>
                ))}
              </div>
            </div>
            <div className="ab-control-group">
              <span className="ab-section-label">{t('audiobook.controls.emotion')}</span>
              <div className="ab-pills">
                {AUDIOBOOK_EMOTION_OPTIONS.map((opt) => (
                  <button key={opt.id} type="button" className={`ab-pill ${emotionIntensity === opt.id ? 'active' : ''}`} onClick={() => setEmotionIntensity(opt.id)} disabled={activeRunBusy}>{opt.label}</button>
                ))}
              </div>
            </div>
            <div className="ab-control-group">
              <span className="ab-section-label">{t('audiobook.controls.format')}</span>
              <div className="ab-pills">
                {AUDIOBOOK_AUDIO_FORMATS.map((opt) => (
                  <button key={opt.id} type="button" className={`ab-pill ${audioFormat === opt.id ? 'active' : ''}`} onClick={() => setAudioFormat(opt.id)} disabled={activeRunBusy}>{opt.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Chapter Scope */}
          <div className="ab-scope">
            <div className="ab-scope-bar">
              <div className="ab-scope-bar__left">
                <span className="ab-section-label" style={{ margin: 0 }}>{t('audiobook.scope.chaptersLabel')}</span>
                <span className="ab-scope-bar__label">
                  {scopeMode === 'full' ? t('audiobook.scope.allSelected') : t('audiobook.scope.selectedOf', { selected: selectedChapterPaths.length, total: allChapterPaths.length })}
                </span>
              </div>
              <div className="ab-pills">
                <button type="button" className={`ab-pill ${scopeMode === 'full' ? 'active' : ''}`} onClick={() => setScopeMode('full')} disabled={activeRunBusy}>{t('audiobook.scope.fullBook')}</button>
                <button type="button" className={`ab-pill ${scopeMode === 'selected' ? 'active' : ''}`} onClick={() => setScopeMode('selected')} disabled={activeRunBusy}>{t('audiobook.scope.pickChapters')}</button>
              </div>
            </div>
            {scopeMode === 'selected' && (
              <div className="ab-chapter-chips">
                {chapterFiles.map((chapter) => (
                  <button key={chapter.path} type="button" className={`ab-chip ${selectedChapterPaths.includes(chapter.path) ? 'active' : ''}`} onClick={() => toggleChapter(chapter.path)} disabled={activeRunBusy}>
                    {chapter.path.replace('Chapters/', '')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Collapsible: Pronunciation Glossary */}
          <div className="ab-collapse">
            <button type="button" className="ab-collapse__trigger" onClick={() => setGlossaryOpen((prev) => !prev)}>
              <span className="ab-collapse__title">{t('audiobook.glossary.title')}</span>
              <span className={`ab-collapse__chevron ${glossaryOpen ? 'open' : ''}`}>&#9656;</span>
            </button>
            {glossaryOpen && (
              <div className="ab-collapse__body">
                <textarea
                  className="ab-textarea"
                  value={glossaryText}
                  onChange={(event) => setGlossaryText(event.target.value)}
                  placeholder={t('audiobook.glossary.placeholder')}
                  disabled={activeRunBusy}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* ══ Voice & Casting Tab ══ */}
      {activeTab === 'casting' ? (
        <div className="ab-panel-fade" role="tabpanel" aria-label={t('audiobook.tabs.voiceAndCasting')}>

          {/* Narrator Voice */}
          <div className="ab-cast-section">
            <div className="ab-cast-section__head">
              <div>
                <h2>{t('audiobook.casting.narratorVoice')}</h2>
                <p>{t('audiobook.casting.narratorDesc')}</p>
              </div>
            </div>
            <div className="ab-voices">
              {AUDIOBOOK_VOICES.map((voice) => (
                <div
                  key={voice.id}
                  className={`ab-voice ${narratorVoice === voice.id ? 'active' : ''} ${activeRunBusy ? 'disabled' : ''}`}
                  onClick={() => !activeRunBusy && setNarratorVoice(voice.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!activeRunBusy) setNarratorVoice(voice.id); } }}
                >
                  <span className="ab-voice__name">{voice.label}</span>
                  <span className="ab-voice__tone">{voice.tone}</span>
                  <span className="ab-voice__desc">{voice.bestFor}</span>
                  <button
                    type="button"
                    className="ab-voice__preview"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewVoice(voice.id);
                    }}
                    disabled={previewingVoice !== null}
                  >
                    {previewingVoice === voice.id ? t('audiobook.casting.loadingPreview') : t('audiobook.casting.preview')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Character Casting */}
          <div className="ab-cast-section">
            <div className="ab-cast-section__head">
              <div>
                <h2>{t('audiobook.casting.characterCasting')}</h2>
                <p>{t('audiobook.casting.characterDesc')}</p>
              </div>
              <button
                className="btn btn-secondary"
                onClick={handleAutoCast}
                disabled={!isBackendReady || isCasting || activeRunBusy}
              >
                {isCasting ? t('audiobook.casting.castingReady') : t('audiobook.casting.autoCastAll')}
              </button>
            </div>
            <div className="ab-cast-list">
              {characterCandidates.length === 0 ? (
                <div className="ab-empty-note">
                  {t('audiobook.casting.noCharacters')}
                </div>
              ) : (
                characterCandidates.map((character) => {
                  const assignment = castingAssignments.find((item) => item.character === character);
                  return (
                    <div key={character} className="ab-cast-row">
                      <div>
                        <div className="ab-cast-row__name">{character}</div>
                        <div className="ab-cast-row__reason">{assignment?.rationale || t('audiobook.casting.noVoiceAssigned')}</div>
                      </div>
                      <div className="ab-cast-row__pills">
                        {AUDIOBOOK_VOICES.map((voice) => (
                          <button
                            key={voice.id}
                            type="button"
                            className={`ab-mini-pill ${assignment?.voiceId === voice.id ? 'active' : ''}`}
                            onClick={() => setCharacterVoice(character, voice.id)}
                            disabled={activeRunBusy}
                          >
                            {voice.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* ══ Timeline Tab ══ */}
      {activeTab === 'timeline' ? (
        <div className="ab-panel-fade" role="tabpanel" aria-label={t('audiobook.tabs.timeline')}>
          <div className="ab-timeline-header">
            <div className="ab-timeline-header__left">
              <h2>{t('audiobook.timeline.renderTimeline')}</h2>
              {runForDisplay && (
                <span className={`ab-status ab-status--${runForDisplay.status}`}>
                  {formatStatusLabel(runForDisplay.status, t)}
                </span>
              )}
            </div>
            <select
              className="ab-run-select"
              value={runForDisplay?._id ?? ''}
              onChange={(event) => setActiveRunId(event.target.value || null)}
            >
              <option value="">{t('audiobook.timeline.latestRun')}</option>
              {(runs || []).map((run) => (
                <option key={run._id} value={run._id}>
                  {formatRunTimestamp(run.createdAt, t, i18n.language || undefined)} · {formatStatusLabel(run.status, t)}
                </option>
              ))}
            </select>
          </div>

          {runForDisplay && timelineItems.length > 0 ? (
            <div className="ab-timeline">
              {timelineItems.map((state) => {
                const asset = assetByChapterPath[state.chapterPath];
                return (
                  <article key={state.chapterPath} className="ab-track">
                    <div className="ab-track__top">
                      <span className="ab-track__name">
                        {state.chapterPath.replace('Chapters/', '')}
                      </span>
                      <span className={`ab-status ab-status--${state.status}`}>
                        {formatStatusLabel(state.status, t)}
                      </span>
                    </div>
                    <p className="ab-track__meta">
                      {state.summary || t('audiobook.timeline.awaitingRender')} · {formatDurationLabel(state.estimatedDurationSeconds || 0)}
                    </p>
                    {state.error && <p className="ab-track__error">{state.error}</p>}

                    {asset?.url ? (
                      <div className="ab-track__player">
                        <audio controls preload="none" src={asset.url} />
                        <a className="btn btn-secondary btn-sm" href={asset.url} target="_blank" rel="noreferrer">
                          {t('common.download')}
                        </a>
                        <button className="btn btn-secondary btn-sm" onClick={() => setShareModal({ body: t('audiobook.timeline.shareClipMessage', { chapter: state.chapterPath?.split('/').pop()?.replace('.md', '') || t('audiobook.timeline.chapterFallback'), title: project?.title || t('audiobook.timeline.bookFallback') }), audioStorageId: asset.storageId })}>
                          {t('audiobook.timeline.shareClip')}
                        </button>
                      </div>
                    ) : (
                      <span className="ab-muted">{t('audiobook.timeline.noAudioYet')}</span>
                    )}

                    {asset?.transcript && (
                      <details className="ab-track__transcript">
                        <summary>{t('audiobook.timeline.viewTranscript')}</summary>
                        <p>{asset.transcript.slice(0, 1200)}{asset.transcript.length > 1200 ? '...' : ''}</p>
                      </details>
                    )}
                  </article>
                );
              })}
            </div>
          ) : runForDisplay ? (
            <div className="ab-empty">
              <h3>{t('audiobook.timeline.noAudioTracks')}</h3>
              <p>{t('audiobook.timeline.noAudioTracksDesc')}</p>
            </div>
          ) : (
            <div className="ab-empty">
              <h3>{t('audiobook.timeline.noRenders')}</h3>
              <p>{t('audiobook.timeline.noRendersDesc')}</p>
            </div>
          )}
        </div>
      ) : null}

      </section>

      {shareModal && (
        <CommunityShareModal
          projectId={safeProjectId}
          initialBody={shareModal.body || ''}
          audioStorageId={shareModal.audioStorageId}
          genre={project?.genre}
          onClose={() => setShareModal(null)}
          onSuccess={(msg) => setSuccessMessage(msg)}
        />
      )}
    </main>
  );
}

export default AudiobookStudioPage;
